import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { getLenis } from "./SmoothScroll";
import { PROFILE } from "../lib/content";

const SECTIONS = [
  { label: "Expertise", id: "expertise" },
];
const ROUTES = [
  { label: "Practice", to: "/practice" },
  { label: "Blog", to: "/blog" },
  { label: "Testimonials", to: "/testimonials" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  const lenis = getLenis();
  if (el) {
    if (lenis) lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      scrollToId(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass border-b border-gold/10" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            onClick={() => { const l = getLenis(); if (l && location.pathname === "/") l.scrollTo(0); }}
            className="flex items-center gap-3 group"
          >
            <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold font-serif text-lg group-hover:bg-gold group-hover:text-ink transition-colors duration-300">
              Z
            </span>
            <span className="hidden sm:block font-serif text-xl tracking-wide text-foreground">
              Dr. Zeenal Punamiya
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => goToSection(s.id)}
                data-testid={`nav-${s.id}`}
                className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-gold transition-colors duration-300"
              >
                {s.label}
              </button>
            ))}
            {ROUTES.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                data-testid={`nav-${r.label.toLowerCase()}`}
                className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-gold transition-colors duration-300"
              >
                {r.label}
              </Link>
            ))}
            <Link
              to="/book"
              data-testid="nav-book-btn"
              className="px-6 py-2.5 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.15em] font-semibold hover:bg-gold-light transition-colors duration-300"
            >
              Book Consultation
            </Link>
          </div>

          <button
            className="lg:hidden text-foreground"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-ink lg:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-gold/10">
              <span className="font-serif text-xl text-foreground">Menu</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={26} className="text-foreground" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-6 px-8">
              {SECTIONS.map((s, i) => (
                <motion.button
                  key={s.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => goToSection(s.id)}
                  className="text-left font-serif text-4xl text-foreground hover:text-gold transition-colors"
                >
                  {s.label}
                </motion.button>
              ))}
              {ROUTES.map((r, i) => (
                <motion.div
                  key={r.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * (i + SECTIONS.length) }}
                >
                  <Link
                    to={r.to}
                    onClick={() => setOpen(false)}
                    className="font-serif text-4xl text-foreground hover:text-gold transition-colors"
                  >
                    {r.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/book"
                onClick={() => setOpen(false)}
                data-testid="mobile-book-btn"
                className="mt-6 px-6 py-4 rounded-full bg-gold text-ink text-center uppercase tracking-[0.15em] font-semibold"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
